import { Button } from '@/components/ui/button';
import { DashboardCard } from '@/components/ui/dashboard-card';
import { Edit2 } from 'lucide-react';
import React from 'react';
import { FormValues, TemplateData } from '../types';
import { AddSectionButton } from './AddSectionButton';
import { FieldRenderer } from './FieldRenderer';

interface EntityFieldsTabProps {
  templateData: TemplateData;
  mode: 'preview' | 'edit';
  formValues: FormValues;
  isSaving: boolean;
  onModeChange: (mode: 'preview' | 'edit') => void;
  onSaveChanges: () => void;
  onInputChange: (fieldId: number, value: string | null) => void;
  onRefreshData?: () => Promise<void>;
  layout?: 'main' | 'sidebar' | 'full';
}

/**
 * Renders a single field row — uniform stacked layout for ALL field types.
 * Label on top, value below — consistent across every section.
 */
const FieldRow = ({
  field,
  formValues,
  mode,
  onInputChange,
}: {
  field: any;
  formValues: FormValues;
  mode: 'preview' | 'edit';
  onInputChange: (fieldId: number, value: string | null) => void;
}) => {
  return (
    <div className="group flex flex-col gap-2 px-6 py-4 hover:bg-muted/5 transition-colors">
      <span className="text-[11px] font-bold uppercase tracking-widest text-muted-foreground/70">
        {field.field_name}
        {field.is_required && <span className="text-destructive ml-0.5">*</span>}
      </span>
      <div>
        <FieldRenderer
          field={field}
          value={formValues[field.id] || ''}
          isEditing={mode === 'edit'}
          onChange={onInputChange}
        />
      </div>
      {field.help_text && mode === 'edit' && (
        <p className="text-[10px] text-muted-foreground/50 italic">{field.help_text}</p>
      )}
    </div>
  );
};

export const EntityFieldsTab: React.FC<EntityFieldsTabProps> = ({
  templateData,
  mode,
  formValues,
  onInputChange,
  onRefreshData,
  onModeChange,
  layout = 'main'
}) => {
  const entity = templateData.entity;
  const template = templateData.template;
  const templateId = template?.id;
  const entityId = entity?.id;

  const fieldsWithoutSection = templateData.fieldsWithoutSection || [];
  const sections = templateData.sections || [];

  // For sidebar layout, only show non-textarea-heavy sections
  const visibleSections = sections.filter(section => {
    if (layout === 'full') return true;
    const hasTextarea = section.template_fields?.some((f: any) => f.field_type === 'textarea');
    return layout === 'main' ? hasTextarea : !hasTextarea;
  });

  const showBasicInfo = (layout === 'sidebar' || layout === 'full') && fieldsWithoutSection.length > 0;

  const editAction = mode === 'preview' ? (
    <Button variant="ghost" size="sm" onClick={() => onModeChange('edit')} className="h-8 w-8 p-0">
      <Edit2 className="h-4 w-4 text-muted-foreground" />
    </Button>
  ) : null;

  return (
    <React.Fragment>
      {/* Basic Information */}
      {showBasicInfo && (
        layout === 'sidebar' ? (
          /* Sidebar — compact list */
          <div className="rounded-xl border border-border bg-card shadow-sm overflow-hidden">
            <div className="px-4 py-3 border-b border-border bg-muted/40">
              <h3 className="text-sm font-semibold tracking-tight">Vitals</h3>
            </div>
            <div className="flex flex-col divide-y divide-border/50">
              {fieldsWithoutSection.map((field) => (
                <div
                  key={field.id}
                  className="group flex flex-row items-center justify-between p-3 hover:bg-muted/20 transition-colors"
                >
                  <span className="text-xs font-medium text-muted-foreground">
                    {field.field_name}
                    {field.is_required && <span className="text-destructive ml-0.5">*</span>}
                  </span>
                  <div className="text-right max-w-[60%]">
                    <FieldRenderer
                      field={field}
                      value={formValues[field.id] || ''}
                      isEditing={mode === 'edit'}
                      onChange={onInputChange}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : (
          /* Full layout — DashboardCard, uniform divided rows */
          <DashboardCard
            title="Basic Information"
            count={fieldsWithoutSection.length}
            className="min-h-[100px]"
            action={editAction}
          >
            <div className="flex flex-col divide-y divide-border">
              {fieldsWithoutSection.map((field) => (
                <FieldRow
                  key={field.id}
                  field={field}
                  formValues={formValues}
                  mode={mode}
                  onInputChange={onInputChange}
                />
              ))}
            </div>
          </DashboardCard>
        )
      )}

      {/* Dynamic Sections — all use the same divided-row DashboardCard style */}
      {visibleSections.map((section) => {
        if (layout === 'sidebar') {
          return (
            <div key={section.id} className="rounded-xl border border-border bg-card shadow-sm overflow-hidden">
              <div className="px-4 py-3 border-b border-border bg-muted/40 flex justify-between items-center">
                <h3 className="text-sm font-semibold tracking-tight">{section.name}</h3>
              </div>
              <div className="flex flex-col divide-y divide-border/50">
                {section.template_fields?.map((field: any) => (
                  <div key={field.id} className="group flex flex-row items-center justify-between p-3 hover:bg-muted/20 transition-colors">
                    <span className="text-xs font-medium text-muted-foreground">{field.field_name}</span>
                    <div className="text-right max-w-[60%]">
                      <FieldRenderer
                        field={field}
                        value={formValues[field.id] || ''}
                        isEditing={mode === 'edit'}
                        onChange={onInputChange}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          );
        }

        return (
          <DashboardCard
            key={section.id}
            title={section.name}
            count={section.template_fields?.length}
            className="min-h-[100px]"
            action={editAction}
          >
            <div className="flex flex-col divide-y divide-border">
              {section.template_fields?.map((field: any) => (
                <FieldRow
                  key={field.id}
                  field={field}
                  formValues={formValues}
                  mode={mode}
                  onInputChange={onInputChange}
                />
              ))}
            </div>
          </DashboardCard>
        );
      })}

      {/* Empty State / Add Section */}
      {(layout === 'main' || layout === 'full') && mode === 'edit' && templateId && entityId && (
        <div className="min-h-[100px] flex items-center justify-center border-2 border-dashed border-border/40 rounded-2xl hover:bg-muted/5 transition-all cursor-pointer">
          <AddSectionButton
            templateId={templateId}
            entityId={entityId}
            onSuccess={onRefreshData}
          />
        </div>
      )}
    </React.Fragment>
  );
};

export default EntityFieldsTab;
